import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, MessageSquare, Inbox, ArrowLeft, Phone, Mail, User, Bot, BookOpen } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { KnowledgeBaseEditor } from "./KnowledgeBaseEditor";

const fmt = (ts) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const SourceBadge = ({ source }) => {
  const callback = source === "ai_assistant_callback";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${callback ? "bg-gold/25 text-gold-700" : "bg-navy/10 text-navy"}`}>
      {callback ? "Callback" : "AI Lead"}
    </span>
  );
};

export const AssistantLeadsPanel = () => {
  const [view, setView] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // {session_id, messages}
  const [loadingConvo, setLoadingConvo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/admin/assistant/leads"),
        api.get("/admin/assistant/sessions"),
      ]);
      setLeads(l.data);
      setSessions(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openConversation = async (session_id) => {
    setLoadingConvo(true);
    setActive({ session_id, messages: [] });
    try {
      const { data } = await api.get(`/admin/assistant/conversation/${session_id}`);
      setActive({ session_id, messages: data });
    } catch (err) {
      toast.error("Could not load conversation");
      setActive(null);
    } finally {
      setLoadingConvo(false);
    }
  };

  const isPriority = (n) => /EMERGENCY|HIGH PRIORITY/i.test(n?.notes || "");

  return (
    <div data-testid="assistant-admin-panel">
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="flex rounded-full bg-white p-1 shadow-sm">
          <button onClick={() => setView("leads")} data-testid="assistant-subtab-leads"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition ${view === "leads" ? "bg-navy text-white" : "text-navy/70 hover:text-navy"}`}>
            <Inbox size={15} /> AI Leads <span className="rounded-full bg-black/10 px-1.5 text-xs">{leads.length}</span>
          </button>
          <button onClick={() => setView("conversations")} data-testid="assistant-subtab-conversations"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition ${view === "conversations" ? "bg-navy text-white" : "text-navy/70 hover:text-navy"}`}>
            <MessageSquare size={15} /> Conversations <span className="rounded-full bg-black/10 px-1.5 text-xs">{sessions.length}</span>
          </button>
          <button onClick={() => setView("kb")} data-testid="assistant-subtab-kb"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition ${view === "kb" ? "bg-navy text-white" : "text-navy/70 hover:text-navy"}`}>
            <BookOpen size={15} /> Knowledge Base
          </button>
        </div>
        {view !== "kb" && (
          <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20">
            <RefreshCw size={14} /> Refresh
          </button>
        )}
      </div>

      {view === "kb" ? (
        <KnowledgeBaseEditor />
      ) : loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>
      ) : view === "leads" ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {leads.length === 0 ? (
            <p className="p-10 text-center text-sm text-muted-foreground">No AI Assistant leads yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-navy-50 text-xs uppercase tracking-wide text-navy">
                  <tr><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Details</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Chat</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map((q) => (
                    <tr key={q.id} data-testid={`ai-lead-row-${q.id}`} className={`align-top hover:bg-gray-50 ${isPriority(q) ? "bg-red-50/60" : ""}`}>
                      <td className="px-4 py-3">
                        <p className="font-bold text-navy">{q.name}</p>
                        <p className="text-xs text-muted-foreground">{q.phone}</p>
                        {q.email && !q.email.includes("not-provided") && <p className="text-xs text-muted-foreground">{q.email}</p>}
                      </td>
                      <td className="px-4 py-3"><SourceBadge source={q.source} />{isPriority(q) && <span className="ml-1 rounded-full bg-brandred px-2 py-0.5 text-[11px] font-bold text-white">Priority</span>}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground"><p>{q.shipping_from}</p>{q.notes && <p className="mt-1 italic">"{q.notes}"</p>}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(q.created_at)}</td>
                      <td className="px-4 py-3">
                        {q.session_id ? (
                          <button onClick={() => { setView("conversations"); openConversation(q.session_id); }} data-testid={`ai-lead-chat-${q.id}`}
                            className="rounded-lg border border-navy/20 px-2.5 py-1 text-xs font-bold text-navy hover:bg-navy-50">View chat</button>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : active ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="conversation-view">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-navy-50 px-4 py-3">
            <button onClick={() => setActive(null)} data-testid="conversation-back" className="inline-flex items-center gap-1.5 text-sm font-bold text-navy hover:text-brandred"><ArrowLeft size={15} /> Back</button>
            <span className="text-xs text-muted-foreground">Session {active.session_id.slice(0, 8)}…</span>
          </div>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto bg-navy-50/40 p-4">
            {loadingConvo ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
            ) : active.messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${m.role === "user" ? "rounded-br-sm bg-brandred text-white" : "rounded-tl-sm bg-white text-navy"}`}>
                  <div className="mb-1 flex items-center gap-1 text-[10px] uppercase opacity-60">{m.role === "user" ? <User size={11} /> : <Bot size={11} />}{m.role === "user" ? "Customer" : "Assistant"}</div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {sessions.length === 0 ? (
            <p className="p-10 text-center text-sm text-muted-foreground">No conversations yet.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {sessions.map((s) => (
                <button key={s.session_id} onClick={() => openConversation(s.session_id)} data-testid={`conversation-row-${s.session_id.slice(0,8)}`}
                  className="flex w-full items-center gap-4 px-4 py-3.5 text-left hover:bg-gray-50">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy/10 text-navy"><MessageSquare size={18} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-navy"><span className="font-bold">{s.message_count} msgs</span> · {s.last_message?.slice(0, 90) || "…"}</p>
                    <p className="text-xs text-muted-foreground">{fmt(s.last_ts)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
